import {
  ComponentType,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactSelect, {
  ControlProps,
  IndicatorProps,
  InputProps,
  MenuProps,
  MultiValueProps,
  OptionProps,
  OptionTypeBase,
  PlaceholderProps,
  Props as SelectProps,
  SingleValueProps,
  ValueContainerProps,
  components,
} from "react-select";
import ReactSelectAsync, {
  Props as ReactSelectAsyncProps,
} from "react-select/async";
import CreatableSelect from "react-select/creatable";
import {
  MultiValueGenericProps,
  MultiValueRemoveProps,
} from "react-select/src/components/MultiValue";

import Tooltip from "@/components/elements/Tooltip";
import cn from "@/helpers/classNames";
import { DropdownOption } from "@/integrations/forms";
import { useTheme } from "@/providers/ThemeProvider";
import mergeRefs from "@/utils/mergeRefs";
import { CheckIcon, ChevronsUpDownIcon, XIcon } from "lucide-react";

import InputContainerButton from "./InputContainer";

export { AppliedMultiSelect } from "./AppliedMultiSelect";

const Control = ({ children, ...props }: ControlProps<any, false>) => {
  const {
    selectProps: { descriptionKey },
    isDisabled,
  } = props;
  const hasDescription = useMemo(
    () => !!props.options?.some((v) => v[descriptionKey]),
    [descriptionKey, props.options],
  );
  return (
    <components.Control {...props}>
      <InputContainerButton
        isDisabled={isDisabled}
        className={cn(`py-2.5`, {
          "h-16": hasDescription,
          "h-auto": props.isMulti,
        })}
        isFocused={props.isFocused}
      >
        <>{children}</>
      </InputContainerButton>
    </components.Control>
  );
};

const DropdownIndicator = (props: IndicatorProps<any, false>) => {
  return (
    <components.DropdownIndicator {...props} className="pr-2">
      <ChevronsUpDownIcon
        className="size-5 text-gray-400 transition-all duration-300 group-hover:text-gray-500"
        aria-hidden="true"
      />
    </components.DropdownIndicator>
  );
};

const SelectContainer = ({ children, ...props }: any) => (
  <components.SelectContainer {...props}>{children}</components.SelectContainer>
);

const ValueContainer = (props: ValueContainerProps<any, false>) => (
  <components.ValueContainer
    {...props}
    className={`flex grow flex-wrap gap-1 pl-2`}
  />
);

const Badge = ({ children }: { children: string }) => {
  if (!children) return null;
  return (
    <Tooltip content={children.toLowerCase()}>
      <span className="truncate rounded-sm bg-gray-100 px-1 text-xs text-gray-900">
        {children.toLowerCase()}
      </span>
    </Tooltip>
  );
};

const SelectItemContent = (props: any) => {
  const {
    data,
    selectProps: { descriptionKey, descriptionParser, badgeKey, iconParser },
    isFocused,
    label,
  } = props;

  return (
    <>
      {iconParser && data && (
        <div className="shrink-0 pr-2">{iconParser(data)}</div>
      )}

      <span className="flex flex-col truncate">
        <Tooltip content={label}>
          <span className="truncate">{label}</span>
        </Tooltip>

        {descriptionKey && (
          <span
            className={cn("truncate text-sm text-muted-foreground", {
              "text-white": isFocused,
            })}
          >
            {descriptionParser
              ? descriptionParser(data[descriptionKey])
              : data[descriptionKey]}
          </span>
        )}
      </span>

      <Badge>{data[badgeKey]}</Badge>

      {data?.readonly && <Badge>readonly</Badge>}

      {data?.isRequired && <Badge>Required</Badge>}
    </>
  );
};

const SingleValue = ({
  children: _children,
  ...props
}: SingleValueProps<any>) => {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center space-x-1 text-gray-800 dark:text-white">
        <SelectItemContent
          {...props}
          label={props.selectProps.getOptionLabel?.(props.data)}
        />
      </div>
    </components.SingleValue>
  );
};

const Menu = ({ children, ...props }: MenuProps<any, false>) => (
  <components.Menu {...props}>
    {props.selectProps.isLoading ? (
      <div className="p-2 text-center text-base leading-6 text-gray-400 dark:bg-gray-700">
        Loading...
      </div>
    ) : (
      children
    )}
  </components.Menu>
);

const Option = ({ innerProps, ...props }: OptionProps<any, false>) => {
  const {
    data,
    isFocused,
    isDisabled,
    selectOption,
    isSelected,
    selectProps: { disableCheckmark },
  } = props;
  const addIndentToLabel = (data: DropdownOption): string => {
    return data.indent
      ? `\u2514${"\u2500".repeat(data.indent || 0)} ${props.label}`
      : props.label;
  };

  return (
    <div
      {...innerProps}
      className={cn("relative cursor-default px-4 py-2 select-none", {
        "bg-accent": isSelected || isFocused,
        "cursor-not-allowed opacity-25": isDisabled,
      })}
      onClick={() => {
        selectOption(data);
      }}
    >
      <div className={cn(`flex items-center space-x-1 truncate`)}>
        <SelectItemContent {...props} label={addIndentToLabel(data)} />
        <div className="grow" />
        {isSelected && !disableCheckmark ? (
          <span className="flex items-center">
            <CheckIcon className="size-5" aria-hidden="true" />
          </span>
        ) : null}
      </div>
    </div>
  );
};

const Placeholder = (props: PlaceholderProps<any, false>) => {
  return (
    <div className="absolute inset-0 flex flex-col justify-center truncate px-2 text-sm text-gray-400">
      <div className="truncate">{props.children}</div>
    </div>
  );
};

const ClearIndicator = (props: IndicatorProps<any, false>) => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref}>
      <XIcon className={`size-4 text-gray-400 hover:text-gray-500`} />
    </div>
  );
};

const MultiValue = (props: MultiValueProps<any>) => (
  <components.MultiValue
    {...props}
    className={`${
      props.isFocused ? "bg-blue-300" : "bg-black dark:bg-gray-700"
    } flex items-center overflow-hidden rounded-xs text-xs text-white`}
  />
);

const MultiValueContainer = ({
  children,
  ...props
}: MultiValueGenericProps<any>) => (
  <components.MultiValueContainer {...props}>
    <div
      className={`flex cursor-pointer items-center space-x-1 py-0.5 pr-1 pl-2 transition-opacity hover:opacity-80`}
    >
      {children}
    </div>
  </components.MultiValueContainer>
);

const MultiValueRemove = ({
  children: _children,
  ...props
}: MultiValueRemoveProps<any>) => (
  <components.MultiValueRemove {...props}>
    <XIcon className={`size-3`} />
  </components.MultiValueRemove>
);

const Input = ({
  isMulti: _isMulti,
  hasValue: _hasValue,
  ...props
}: InputProps & { isMulti: boolean; hasValue: boolean }) => (
  <components.Input {...props} className="h-5" />
);

const Select = forwardRef(
  <
    OptionType extends OptionTypeBase = { label: string; value: string },
    IsMulti extends boolean = false,
  >(
    {
      autoFocus,
      onChange = () => null,
      options = [],
      disabled = false,
      labelKey = "label",
      descriptionKey = "",
      badgeKey = "badge",
      creatable = false,
      isClearable = true,
      value,
      placeholder,
      getOptionLabel,
      autoSelectOnSingleOption = false,
      disableCheckmark = false,
      styles,
      components,
      ...rest
    }: SelectProps<OptionType, IsMulti>,
    forwardRef: React.Ref<any>,
  ) => {
    const { isDarkMode } = useTheme();
    const ref = useRef<CreatableSelect<any>>();

    // Fix from https://github.com/JedWatson/react-select/issues/165 (makes headlessui transitions and isSearchable work together)
    const [isSearchable, setIsSearchable] = useState(false);
    useEffect(() => {
      setTimeout(() => {
        setIsSearchable(rest.isSearchable === false ? false : true);
      }, 1);
    }, [rest.isSearchable]);

    useEffect(() => {
      if (!autoFocus) return;

      setTimeout(() => {
        ref.current?.focus();
      });
    }, [autoFocus]);

    useAutoSelectSingleOption(
      options,
      onChange,
      autoSelectOnSingleOption,
      rest.isMulti,
    );

    const Select = {
      creatable: CreatableSelect,
      default: ReactSelect,
    }[creatable ? "creatable" : "default"];

    return (
      // @ts-expect-error -- labelKey/badgeKey/descriptionKey/disableCheckmark are
      // consumed by our custom option components, not by react-select's own props,
      // and the CreatableSelect | ReactSelect union rejects them.
      <Select
        ref={mergeRefs(forwardRef, ref)}
        onChange={onChange}
        descriptionKey={descriptionKey}
        badgeKey={badgeKey}
        labelKey={labelKey}
        options={options}
        disableCheckmark={disableCheckmark}
        styles={{
          menuList: (base: any) => ({
            ...base,
            textSize: "10px",
            padding: 0,
            ...(isDarkMode && {
              background: "rgb(62, 63, 65)",
            }),
          }),
          menu: (base: any) => ({
            ...base,
            overflow: "hidden",
            minWidth: "100%",
            width: "fit-content",
          }),
          control: () => null,
          input: () => ({
            ...(isDarkMode && { color: "#fff" }),
          }),
          valueContainer: () => null,
          indicatorsContainer: (base: any) => ({ ...base, padding: undefined }),
          dropdownIndicator: (base: any) => ({ ...base, padding: undefined }),
          clearIndicator: (base: any) => ({ ...base, padding: undefined }),
          loadingIndicator: (base: any) => ({ ...base, padding: undefined }),
          multiValue: () => null,
          multiValueLabel: () => null,
          multiValueRemove: () => null,
          ...(styles || {}),
        }}
        menuPlacement="auto"
        isSearchable={isSearchable}
        isOptionDisabled={(option: any) => option.disabled}
        isDisabled={disabled}
        isClearable={isClearable}
        getOptionLabel={getOptionLabel || ((o: any) => o[labelKey] || o.label)}
        getOptionValue={(o: any) => o.id || o.value}
        menuPortalTarget={document.querySelector("#selecter_drawer")}
        components={{
          Option,
          Control,
          SelectContainer,
          ValueContainer,
          SingleValue,
          Placeholder,
          Menu,
          DropdownIndicator,
          IndicatorSeparator: () => null,
          ClearIndicator,
          MultiValue,
          MultiValueContainer,
          MultiValueRemove,
          Input,
          ...(components || {}),
        }}
        value={value || null}
        placeholder={
          placeholder || (creatable ? "Select or create..." : "Select...")
        }
        {...rest}
      />
    );
  },
);

export default Select;

export function AsyncSelect<OptionType extends OptionTypeBase>(
  props: ReactSelectAsyncProps<OptionType, false>,
) {
  const { isDarkMode } = useTheme();
  return (
    <ReactSelectAsync
      {...props}
      styles={{
        menuList: (base: any) => ({
          ...base,
          textSize: "10px",
          padding: 0,
          ...(isDarkMode && {
            background: "rgb(62, 63, 65)",
          }),
        }),
        menu: (base: any) => ({
          ...base,
          overflow: "hidden",
          minWidth: "100%",
          width: "fit-content",
        }),
        control: () => ({}),
        input: () => ({
          ...(isDarkMode && { color: "#fff" }),
        }),
        valueContainer: () => ({}),
        indicatorsContainer: (base: any) => ({ ...base, padding: undefined }),
        dropdownIndicator: (base: any) => ({ ...base, padding: undefined }),
        clearIndicator: (base: any) => ({ ...base, padding: undefined }),
        loadingIndicator: (base: any) => ({ ...base, padding: undefined }),
        multiValue: () => ({}),
        multiValueLabel: () => ({}),
        multiValueRemove: () => ({}),
        ...(props.styles || {}),
      }}
      components={{
        Option,
        Control,
        SelectContainer,
        ValueContainer,
        SingleValue,
        Placeholder,
        Menu,
        DropdownIndicator,
        IndicatorSeparator: () => null,
        ClearIndicator,
        MultiValue,
        MultiValueContainer,
        MultiValueRemove,
        Input: Input as ComponentType<InputProps>,
        ...(props.components || {}),
      }}
    />
  );
}

const useAutoSelectSingleOption = (
  options: any,
  onChange: SelectProps<any, any>["onChange"],
  enableAutoSelect: boolean,
  isMulti?: boolean,
) => {
  const latestValue = useRef<any>();

  useEffect(() => {
    if (!enableAutoSelect) return;

    if (options?.length === 1) {
      const value = options[0];
      if (value !== latestValue.current) {
        latestValue.current = value;
        onChange?.(isMulti ? [value] : value, {
          action: "select-option",
          option: value,
        });
      }
    }
  }, [options, onChange, enableAutoSelect, isMulti]);
};
